import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from '../../api/client';
import { relativeTime } from '../../util/time';
import { describeError } from '../Submit';

interface SourceCounts {
  added: number;
  updated: number;
  skipped: number;
  failed: number;
  total?: number;
}
interface Run {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  counts: Partial<{
    classicdb: SourceCounts;
    fixture: SourceCounts & { build?: string; enchants?: number; recipesRekeyed?: number; consumables?: number; talentsRetired?: number; talentsRekeyed?: number };
    blizzard_api: SourceCounts & { addonUpdated?: number };
    blizzard_portraits: SourceCounts & { addonUpdated?: number };
    byType: Record<string, number>;
    phase: string;
  }>;
  error: string | null;
  startedByBattletag: string | null;
}

export function Seed() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const runs = useQuery({
    queryKey: ['admin', 'seed', 'runs'],
    queryFn: () => apiGet<{ items: Run[] }>('/api/admin/seed/runs'),
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.status === 'running') ? 2000 : false,
  });
  const start = useMutation({
    mutationFn: () => apiSend<{ id: string }>('POST', '/api/admin/seed'),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'seed', 'runs'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const active = runs.data?.items.find((r) => r.status === 'running');
  return (
    <div data-testid="admin-seed">
      <p>
        Loads the WoW: Forever client fixtures (zones, abilities, talents, items; badged
        &ldquo;Forever beta&rdquo; with the client build), the Classic Era classicdb dump for
        what the client does not carry (quests, NPCs, spawns, loot, vendors, trainers), and
        Blizzard media (item icons, NPC portraits, class icons). Forever data wins over the
        Classic dump for the same game id; re-runs update untouched seeded records and never
        overwrite player-edited ones.
      </p>
      <div className="actions">
        <button type="button" onClick={() => start.mutate()} disabled={!!active || start.isPending}>
          Run seed
        </button>
        {active && (
          <span className="muted" role="status" data-testid="seed-status">
            running · phase {active.counts.phase ?? 'starting'}
          </span>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {runs.data && (
        <div className="table-scroll">
          <table className="grid" data-testid="seed-runs">
            <thead>
              <tr>
                <th>Started</th>
                <th>By</th>
                <th>Status</th>
                <th>classicdb</th>
                <th>forever fixtures</th>
                <th>icons</th>
                <th>portraits</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.items.map((r) => (
                <tr key={r.id} data-testid="seed-run" data-status={r.status}>
                  <td>{relativeTime(r.startedAt)}</td>
                  <td>{r.startedByBattletag ?? 'cli'}</td>
                  <td
                    className={
                      r.status === 'failed' ? 'error' : r.status === 'succeeded' ? 'ok' : ''
                    }
                  >
                    {r.status}
                  </td>
                  <td>
                    <Counts c={r.counts.classicdb} />
                  </td>
                  <td>
                    <Counts c={r.counts.fixture} />
                    {r.counts.fixture?.build && (
                      <span className="muted"> (build {r.counts.fixture.build})</span>
                    )}
                    {r.counts.fixture && (
                      <span className="muted" data-testid="seed-fixture-extras">
                        {' '}
                        · {r.counts.fixture.enchants ?? 0} enchants, {r.counts.fixture.consumables ?? 0}{' '}
                        consumables, {r.counts.fixture.recipesRekeyed ?? 0} recipes re-keyed, {r.counts.fixture.talentsRetired ?? 0}{' '}
                        talents retired, {r.counts.fixture.talentsRekeyed ?? 0} talents re-keyed
                      </span>
                    )}
                  </td>
                  <td>
                    <Counts c={r.counts.blizzard_api} />
                    <FromUploads n={r.counts.blizzard_api?.addonUpdated} />
                  </td>
                  <td>
                    <Counts c={r.counts.blizzard_portraits} />
                    <FromUploads n={r.counts.blizzard_portraits?.addonUpdated} />
                  </td>
                  <td>{r.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** How many of a media phase's updates landed on entries cache uploads created. */
function FromUploads({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <span className="muted" data-testid="seed-from-uploads">
      {' '}
      · {n} from uploads
    </span>
  );
}

function Counts({ c }: { c: SourceCounts | undefined }) {
  if (!c) return <span className="muted">-</span>;
  return (
    <span>
      +{c.added} added, {c.updated} updated, {c.skipped} skipped, {c.failed} failed
    </span>
  );
}
